import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, UserPlus, KeyRound, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const [mode, setMode] = useState('login'); // login, register, forgot, reset
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/admin');
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setIsLoading(true);
    try {
      await login(username, password);
      toast.success('¡Bienvenido a FEX.FIT!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Usuario o contraseña incorrectos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!username || !email || !password || !confirmPassword || !name) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/register`, {
        username,
        email,
        password,
        name,
        role: 'vendedor'
      });
      toast.success('¡Cuenta creada exitosamente! Ahora puedes iniciar sesión');
      setMode('login');
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la cuenta');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Por favor ingresa tu email');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/request-password-reset`, { email });
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
        setMode('reset');
        toast.success('Token generado. Ingresa tu nueva contraseña');
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al solicitar recuperación');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      toast.error('Por favor ingresa tu nueva contraseña');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      toast.success('¡Contraseña actualizada! Ahora puedes iniciar sesión');
      setMode('login');
      setNewPassword('');
      setResetToken('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setNewPassword('');
    setResetToken('');
  };

  return (
    <div 
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1614367674345-f414b2be3e5b?crop=entropy&cs=srgb&fm=jpg&q=85)'
        }}
      />
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/85" />
      
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4 animate-slide-up">
        <div className="bg-[#0A0A0A]/90 backdrop-blur-xl border border-zinc-800 rounded-sm p-8 shadow-2xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src={`${process.env.PUBLIC_URL || ''}/fex-logo-light.png`}
              alt="FEX Fitness Experience"
              className="h-20 w-auto mx-auto mb-3 select-none"
              draggable="false"
            />
            <p className="text-zinc-500 text-sm uppercase tracking-widest">
              <span className="text-white font-black">FEX</span><span className="text-[#00FF9F] font-black">.FIT</span> · Sistema de Gestión
            </p>
          </div>

          {/* LOGIN FORM */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-zinc-400 uppercase text-xs tracking-wider">
                  Usuario o Email
                </Label>
                <Input
                  id="username"
                  type="text"
                  data-testid="login-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="usuario o correo@ejemplo.com"
                  className="bg-[#050505] border-zinc-800 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] text-white placeholder:text-zinc-600 rounded-sm h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-400 uppercase text-xs tracking-wider">
                  Contraseña
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-[#050505] border-zinc-800 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] text-white placeholder:text-zinc-600 rounded-sm h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-fast"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={isLoading}
                className="w-full h-12 bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold uppercase tracking-wider rounded-sm primary-glow transition-fast active-scale"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>

              <div className="flex flex-col gap-3 pt-4 border-t border-zinc-800">
                <button
                  type="button"
                  onClick={() => { resetForm(); setMode('register'); }}
                  className="flex items-center justify-center gap-2 text-zinc-400 hover:text-white transition-fast text-sm"
                >
                  <UserPlus size={16} />
                  Crear una cuenta nueva
                </button>
                <button
                  type="button"
                  onClick={() => { resetForm(); setMode('forgot'); }}
                  className="flex items-center justify-center gap-2 text-zinc-400 hover:text-[#FF3B30] transition-fast text-sm"
                >
                  <KeyRound size={16} />
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            </form>
          )}

          {/* REGISTER FORM */}
          {mode === 'register' && (
            <form onSubmit={handleRegister} className="space-y-4">
              <button
                type="button"
                onClick={() => { resetForm(); setMode('login'); }}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-fast text-sm mb-4"
              >
                <ArrowLeft size={16} />
                Volver al inicio
              </button>

              <h2 className="text-xl font-bold text-white mb-4">Crear Cuenta</h2>

              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs tracking-wider">Nombre Completo</Label>
                <Input
                  type="text"
                  data-testid="register-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Juan Pérez"
                  className="bg-[#050505] border-zinc-800 focus:border-[#FF3B30] text-white placeholder:text-zinc-600 rounded-sm h-11"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs tracking-wider">Usuario</Label>
                <Input
                  type="text"
                  data-testid="register-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, '_'))}
                  placeholder="juan_perez"
                  className="bg-[#050505] border-zinc-800 focus:border-[#FF3B30] text-white placeholder:text-zinc-600 rounded-sm h-11"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs tracking-wider">Email</Label>
                <Input
                  type="email"
                  data-testid="register-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className="bg-[#050505] border-zinc-800 focus:border-[#FF3B30] text-white placeholder:text-zinc-600 rounded-sm h-11"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs tracking-wider">Contraseña</Label>
                <Input
                  type="password"
                  data-testid="register-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="bg-[#050505] border-zinc-800 focus:border-[#FF3B30] text-white placeholder:text-zinc-600 rounded-sm h-11"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs tracking-wider">Confirmar Contraseña</Label>
                <Input
                  type="password"
                  data-testid="register-confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="bg-[#050505] border-zinc-800 focus:border-[#FF3B30] text-white placeholder:text-zinc-600 rounded-sm h-11"
                />
              </div>

              <Button
                type="submit"
                data-testid="register-submit-btn"
                disabled={isLoading}
                className="w-full h-12 bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold uppercase tracking-wider rounded-sm mt-4"
              >
                {isLoading ? <Loader2 className="animate-spin" size={20} /> : 'Crear Cuenta'}
              </Button>
            </form>
          )}

          {/* FORGOT PASSWORD FORM */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <button
                type="button"
                onClick={() => { resetForm(); setMode('login'); }}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-fast text-sm mb-4"
              >
                <ArrowLeft size={16} />
                Volver al inicio
              </button>

              <h2 className="text-xl font-bold text-white mb-2">Recuperar Contraseña</h2>
              <p className="text-zinc-500 text-sm mb-4">
                Ingresa el email asociado a tu cuenta para recuperar tu contraseña.
              </p>

              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs tracking-wider">Email</Label>
                <Input
                  type="email"
                  data-testid="forgot-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className="bg-[#050505] border-zinc-800 focus:border-[#FF3B30] text-white placeholder:text-zinc-600 rounded-sm h-12"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold uppercase tracking-wider rounded-sm"
              >
                {isLoading ? <Loader2 className="animate-spin" size={20} /> : 'Recuperar Contraseña'}
              </Button>
            </form>
          )}

          {/* RESET PASSWORD FORM */}
          {mode === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <h2 className="text-xl font-bold text-white mb-2">Nueva Contraseña</h2>
              <p className="text-zinc-500 text-sm mb-4">
                Ingresa tu nueva contraseña.
              </p>

              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase text-xs tracking-wider">Nueva Contraseña</Label>
                <Input
                  type="password"
                  data-testid="reset-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="bg-[#050505] border-zinc-800 focus:border-[#FF3B30] text-white placeholder:text-zinc-600 rounded-sm h-12"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold uppercase tracking-wider rounded-sm"
              >
                {isLoading ? <Loader2 className="animate-spin" size={20} /> : 'Actualizar Contraseña'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
